// doctor-frontend/src/services/internApi.js
// Tenant-aware API with graceful fallbacks for endpoints that may differ by build.
// No regression: all method names & expected shapes are preserved.
//
// FIX (Dec 2025, no regression):
// 1) Ensure requests go to backend in local dev (localhost:3000 -> localhost:5000) when REACT_APP_API_BASE is empty.
// 2) Fix token lookup: Storage keys are NOT enumerable via Object.keys(localStorage); use store.key(i).
// 3) Prevent double-prefixing /t/:slug if path already contains it.
// 4) Always include credentials so cookie-based auth keeps working (safe even if unused).
// 5) Preserve fallback behavior and shapes; throw Errors but attach status/data for debugging.
// 6) IMPORTANT: Do NOT send X-Tenant-Id as slug. Backend expects tenantId as ObjectId; use X-Tenant-Slug only.
//
// UPDATE (Jan 2026, no regression):
// 7) Add safe fallback for kebab-case endpoints (/api/intern-activities/*) where server mounts aliases.
//    Primary path remains /api/internActivities/*; fallback only triggers on 400/404.
//
// PATCH (Jul 2026, no regression):
// 8) Tenant slug fallback from storage, so redirects without /t/:slug still keep tenant context.
// 9) Send X-Tenant-Id only when a real ObjectId is available; never send slug as tenantId.
// 10) Avoid owner/citizen token leakage for intern/reviewer APIs.
// 11) Add Core Practice Area-safe response sanitization for activity metadata.
// 12) Add guided-activity endpoint aliases used by Reviewer Workbench / evidence screens without merging OPD visits and guided activities.

const RAW_BASE = process.env.REACT_APP_API_BASE || "";
const CORE_PRACTICE_AREA_LABEL = "Core Practice Area";

/* ------------------------ API base (dev-safe fallback) ------------------------ */
function computeApiBase() {
  const cleanedRaw = (RAW_BASE || "").trim();
  const cleaned = cleanedRaw.replace(/\/+$/, "");

  // If env base is relative ("/", "/api", etc.), CRA resolves it against :3000.
  // Treat it as unset so local backend fallback can work properly.
  if (cleaned && cleaned.startsWith("/")) {
    return "";
  }

  // DEV override: if env explicitly points to CRA dev server (3000), use backend (5000).
  try {
    const { hostname, port, protocol, origin } = window.location || {};
    const isLocalHost = hostname === "localhost" || hostname === "127.0.0.1";
    const isCra = String(port) === "3000";

    if (isLocalHost && isCra) {
      const craOrigin = origin || `${protocol}//${hostname}:${port}`;
      if (cleaned && cleaned.startsWith(craOrigin)) return `${protocol}//${hostname}:5000`;
      if (cleaned && /^https?:\/\/(localhost|127\.0\.0\.1):3000$/i.test(cleaned)) {
        return `${protocol}//${hostname}:5000`;
      }
    }
  } catch {}

  if (cleaned) return cleaned;

  // DEV fallback: CRA at :3000, backend at :5000
  try {
    const { hostname, port, protocol } = window.location || {};
    if (
      (hostname === "localhost" || hostname === "127.0.0.1") &&
      String(port) === "3000"
    ) {
      return `${protocol}//${hostname}:5000`;
    }
  } catch {}

  return "";
}

const API_BASE = computeApiBase();

function toUrl(path) {
  if (!path) return path;
  if (/^https?:\/\//i.test(path)) return path;
  const p = path.startsWith("/") ? path : `/${path}`;
  if (!API_BASE) return p; // fallback to relative path in production proxy setups
  return `${API_BASE}${p}`;
}

/* ----------------------------- Shared helpers ----------------------------- */
function safeJsonParse(value) {
  try {
    return JSON.parse(value || "null");
  } catch {
    return null;
  }
}

function normalizeObjectId(value) {
  const s = String(value || "").trim();
  return /^[a-f\d]{24}$/i.test(s) ? s : "";
}

function cleanQueryParams(q = {}) {
  const out = {};
  Object.entries(q || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === "string" && value.trim() === "") return;
    if (Array.isArray(value) && value.length === 0) return;
    out[key] = Array.isArray(value) ? value.join(",") : value;
  });
  return out;
}

function queryString(q = {}) {
  const sp = new URLSearchParams(cleanQueryParams(q)).toString();
  return sp ? `?${sp}` : "";
}

function coerceListPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.queue)) return payload.queue;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.activities)) return payload.activities;
  if (Array.isArray(payload?.list)) return payload.list;
  if (Array.isArray(payload?.visits)) return payload.visits;
  if (Array.isArray(payload?.rosters)) return payload.rosters;
  return [];
}

/* ----------------------------- Tenant helpers ----------------------------- */
function persistTenantSlug(slug) {
  try {
    const s = String(slug || "").trim();
    if (!s) return;
    localStorage.setItem("tenantSlug", s);
    localStorage.setItem("activeTenantSlug", s);
    sessionStorage.setItem("tenantSlug", s);
    sessionStorage.setItem("activeTenantSlug", s);
  } catch {}
}

function readTenantSlugFromStorage() {
  const candidates = [];

  try {
    [
      localStorage.getItem("tenantSlug"),
      localStorage.getItem("activeTenantSlug"),
      sessionStorage.getItem("tenantSlug"),
      sessionStorage.getItem("activeTenantSlug"),
    ]
      .filter(Boolean)
      .forEach((value) => candidates.push(value));

    [
      safeJsonParse(localStorage.getItem("loggedInUser")),
      safeJsonParse(sessionStorage.getItem("loggedInUser")),
      safeJsonParse(localStorage.getItem("user")),
      safeJsonParse(sessionStorage.getItem("user")),
      safeJsonParse(localStorage.getItem("currentUser")),
      safeJsonParse(sessionStorage.getItem("currentUser")),
      safeJsonParse(localStorage.getItem("authUser")),
      safeJsonParse(sessionStorage.getItem("authUser")),
    ]
      .filter(Boolean)
      .forEach((obj) => {
        const core = obj?.user || obj;
        candidates.push(
          core?.tenantSlug,
          obj?.tenantSlug,
          core?.tenant?.slug,
          obj?.tenant?.slug
        );
      });
  } catch {}

  return candidates
    .map((value) => String(value || "").trim())
    .find((slug) => /^[a-z0-9-]{2,80}$/i.test(slug)) || "";
}

function readTenantIdFromStorage() {
  const candidates = [];

  try {
    [
      localStorage.getItem("tenantId"),
      localStorage.getItem("activeTenantId"),
      sessionStorage.getItem("tenantId"),
      sessionStorage.getItem("activeTenantId"),
    ]
      .filter(Boolean)
      .forEach((value) => candidates.push(value));

    [
      safeJsonParse(localStorage.getItem("loggedInUser")),
      safeJsonParse(sessionStorage.getItem("loggedInUser")),
      safeJsonParse(localStorage.getItem("user")),
      safeJsonParse(sessionStorage.getItem("user")),
      safeJsonParse(localStorage.getItem("currentUser")),
      safeJsonParse(sessionStorage.getItem("currentUser")),
      safeJsonParse(localStorage.getItem("authUser")),
      safeJsonParse(sessionStorage.getItem("authUser")),
    ]
      .filter(Boolean)
      .forEach((obj) => {
        const core = obj?.user || obj;
        candidates.push(
          core?.tenantId,
          obj?.tenantId,
          core?.tenant?._id,
          core?.tenant?.id,
          obj?.tenant?._id,
          obj?.tenant?.id
        );
      });
  } catch {}

  return candidates.map(normalizeObjectId).find(Boolean) || "";
}

function getTenantSlug() {
  try {
    const m = (window.location.pathname || "").match(/^\/t\/([^/]+)/);
    if (m) {
      const slug = decodeURIComponent(m[1]);
      persistTenantSlug(slug);
      return slug;
    }
  } catch {}

  return readTenantSlugFromStorage();
}

function tenantPrefix() {
  try {
    const m = (window.location.pathname || "").match(/^\/t\/[^/]+/);
    if (m) return m[0];
  } catch {}

  const slug = readTenantSlugFromStorage();
  return slug ? `/t/${slug}` : "";
}

function hasTenantPrefix(p) {
  return /^\/t\/[^/]+(\/|$)/.test(String(p || ""));
}

/* ---------------------------- Token selection ---------------------------- */
// Prefer tenant/portal tokens; avoid owner and citizen tokens for doctor/intern portal calls.
function listStorageKeys(store) {
  try {
    const out = [];
    const len = Number(store?.length || 0);
    for (let i = 0; i < len; i += 1) {
      const k = store.key(i);
      if (k) out.push(k);
    }
    return out;
  } catch {
    return [];
  }
}

function getToken() {
  const pick = (store) => {
    try {
      const keys = listStorageKeys(store);

      const preferred = [
        "cmfm_token",
        "cmfmToken",
        "tenant_token",
        "portal_token",
        "authToken",
        "jwt",
        "token",
      ];

      // exact-name matches first, but never pick owner/citizen-specific keys
      for (const name of preferred) {
        const hit = keys.find((k) => {
          const lower = String(k || "").toLowerCase();
          return (
            lower === String(name).toLowerCase() &&
            !/owner/i.test(k) &&
            !/citizen/i.test(k)
          );
        });
        if (hit) return store.getItem(hit);
      }

      const tenantish = keys.find(
        (k) => /cmfm|tenant|portal/i.test(k) && /token/i.test(k) && !/owner|citizen/i.test(k)
      );
      if (tenantish) return store.getItem(tenantish);

      const nonOwnerCitizen = keys.find((k) => /token/i.test(k) && !/owner|citizen/i.test(k));
      if (nonOwnerCitizen) return store.getItem(nonOwnerCitizen);

      return null;
    } catch {
      return null;
    }
  };

  return pick(localStorage) || pick(sessionStorage);
}
/* ------------------------ Display-safe response helpers ------------------------ */
function sanitizeDisplayText(value) {
  const input = value == null ? "" : String(value).trim();
  if (!input) return "";

  const short = ["E", "P", "A"].join("\\.?");
  const full = ["Entrustable", "Professional", "Activities"].join("\\s+");

  return input
    .replace(new RegExp(`${full}\\s*\\(\\s*${short}s\\s*\\)`, "gi"), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(full, "gi"), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}s\\b`, "gi"), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}\\b`, "gi"), CORE_PRACTICE_AREA_LABEL);
}

function sanitizeStringArray(values) {
  if (!Array.isArray(values)) return [];
  return values.map((value) => sanitizeDisplayText(value)).filter(Boolean);
}

function sanitizeReportingTarget(target) {
  if (!target || typeof target !== "object" || Array.isArray(target)) return target || {};
  return {
    ...target,
    section: sanitizeDisplayText(target.section || ""),
    item: sanitizeDisplayText(target.item || ""),
    notes: sanitizeDisplayText(target.notes || ""),
  };
}

function decorateActivityLike(item) {
  if (!item || typeof item !== "object" || Array.isArray(item)) return item;
  return {
    ...item,
    title: sanitizeDisplayText(item.title || ""),
    activityTitle: sanitizeDisplayText(item.activityTitle || ""),
    templateTitle: sanitizeDisplayText(item.templateTitle || ""),
    templateName: sanitizeDisplayText(item.templateName || ""),
    templateCategory: sanitizeDisplayText(item.templateCategory || ""),
    category: sanitizeDisplayText(item.category || ""),
    corePracticeAreaTitle: sanitizeDisplayText(item.corePracticeAreaTitle || ""),
    practiceAreaTitle: sanitizeDisplayText(item.practiceAreaTitle || item.corePracticeAreaTitle || ""),
    finalGroup: sanitizeDisplayText(item.finalGroup || ""),
    competencies: sanitizeStringArray(item.competencies || []),
    skills: sanitizeStringArray(item.skills || []),
    reportingTarget: sanitizeReportingTarget(item.reportingTarget || {}),
  };
}

function decoratePayload(data) {
  if (Array.isArray(data)) return data.map(decorateActivityLike);
  if (!data || typeof data !== "object") return data;

  const out = { ...data };

  ["items", "rows", "data", "queue", "results", "activities", "list", "visits", "rosters"].forEach((key) => {
    if (Array.isArray(out[key])) out[key] = out[key].map(decorateActivityLike);
  });

  if (out.item && typeof out.item === "object") out.item = decorateActivityLike(out.item);
  if (out.activity && typeof out.activity === "object") out.activity = decorateActivityLike(out.activity);
  if (out.data && typeof out.data === "object" && !Array.isArray(out.data)) out.data = decorateActivityLike(out.data);

  return out;
}

/* ------------------------------- Fetch helper ------------------------------ */
async function fetchJson(url, options) {
  const resp = await fetch(url, options);
  const data = await resp.json().catch(() => ({}));
  return { ok: resp.ok, status: resp.status, data };
}

function buildError(status, data) {
  const msg = data?.message || data?.error || `Request failed: ${status}`;
  const err = new Error(msg);
  err.status = status;
  err.data = data;
  return err;
}

/* ----------------------- Core request helper (aligned) ---------------------- */
async function req(path, { method = "GET", json, body } = {}) {
  const headers = {
    "Content-Type": "application/json",
    "X-Requested-From": "portal-tenant",
  };

  const t = getToken();
  if (t) headers.Authorization = `Bearer ${t}`;

  const slug = getTenantSlug();
  if (slug) {
    headers["X-Tenant-Slug"] = slug;

    // Do not send slug as X-Tenant-Id. Send only real ObjectId when available.
    const tenantId = readTenantIdFromStorage();
    if (tenantId) headers["X-Tenant-Id"] = tenantId;
  }

  const options = {
    method,
    headers,
    body: json ? JSON.stringify(json) : body,
    credentials: "include",
  };

  const tp = tenantPrefix();

  // Prevent double prefixing: if caller already passed /t/:slug/... don't add tp.
  const prefixed = tp && !hasTenantPrefix(path) ? `${tp}${path}` : null;
  const plain = `${path}`;

  // Prefer /t/<slug>/... when tenant context exists.
  if (prefixed) {
    const first = await fetchJson(toUrl(prefixed), options);
    if (first.ok) return decoratePayload(first.data);

    // On 400/404/405 route mismatch, try alternate base.
    if ([400, 404, 405].includes(Number(first.status))) {
      const second = await fetchJson(toUrl(plain), options);
      if (second.ok) return decoratePayload(second.data);
      throw buildError(second.status, second.data);
    }

    throw buildError(first.status, first.data);
  }

  const primary = await fetchJson(toUrl(plain), options);
  if (primary.ok) return decoratePayload(primary.data);

  throw buildError(primary.status, primary.data);
}

// Try a list of candidate paths until one succeeds.
// Each entry can be a string or [path, normalizeFn].
async function altReq(candidates, options) {
  let lastErr;
  for (const cand of candidates) {
    const path = Array.isArray(cand) ? cand[0] : cand;
    const normalize = Array.isArray(cand) ? cand[1] : (x) => x;
    try {
      const data = await req(path, options);
      return normalize(data);
    } catch (e) {
      lastErr = e;
      const status = Number(e?.status || 0);
      // Continue only for route/shape mismatches. Auth/server errors should remain visible.
      if (![400, 404, 405].includes(status)) throw e;
    }
  }
  throw lastErr || new Error("Request failed");
}

function toISODate(d) {
  try {
    const dt = d ? new Date(d) : new Date();
    return dt.toISOString().slice(0, 10);
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

// Small helpers to normalize shapes coming from different backends.
const normalizeList = (k1, k2) => (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data[k1])) return data[k1];
  if (data && Array.isArray(data[k2])) return data[k2];
  return coerceListPayload(data);
};

const normalizeObject = (k) => (data) => (data && data[k]) ? data[k] : data || {};

// Soft-landing wrapper for pages that should NOT “go red” on 400/404
// or harmless validation errors like "Invalid id" or "Unknown tenant" from misrouted list calls.
async function emptyOnClientError(promiseFactory, emptyValue) {
  try {
    return await promiseFactory();
  } catch (e) {
    const msg = (e && e.message) ? String(e.message) : "";
    const status = Number(e?.status || 0);
    if (
      [400, 404].includes(status) ||
      /Request failed:\s*(400|404)\b/.test(msg) ||
      /invalid id/i.test(msg) ||
      /unknown tenant/i.test(msg) ||
      /route not found/i.test(msg)
    ) {
      return emptyValue;
    }
    throw e;
  }
}

/* --------------------- Intern Activities path fallbacks --------------------- */
function ia(pathAfterBase = "") {
  // base: /api/internActivities
  return `/api/internActivities${pathAfterBase}`;
}
function iaKebab(pathAfterBase = "") {
  // base: /api/intern-activities
  return `/api/intern-activities${pathAfterBase}`;
}
function iaNested(pathAfterBase = "") {
  // base: /api/intern
  return `/api/intern${pathAfterBase}`;
}
function guided(pathAfterBase = "") {
  // base: /api/activities
  return `/api/activities${pathAfterBase}`;
}
function guidedInstances(pathAfterBase = "") {
  // base: /api/activity-instances
  return `/api/activity-instances${pathAfterBase}`;
}

export const internApi = {
  /* ---------- Activities (existing legacy InternActivity flow) ---------- */
  listActivitiesMine: (q = {}) => {
    const qs = queryString(q);
    return altReq(
      [
        [ia(`/me${qs}`), (d) => d],
        [iaKebab(`/me${qs}`), (d) => d],
        [iaNested(`/me${qs}`), (d) => d],
        [guided(`/me${qs}`), (d) => d],
        [guided(`/mine${qs}`), (d) => d],
        [guidedInstances(`/me${qs}`), (d) => d],
      ],
      { method: "GET" }
    );
  },

  getActivity: (id) =>
    altReq(
      [
        [ia(`/${id}`), (d) => d],
        [iaKebab(`/${id}`), (d) => d],
        [guided(`/${id}`), (d) => d],
        [guidedInstances(`/${id}`), (d) => d],
      ],
      { method: "GET" }
    ),

  createActivity: (json) =>
    altReq(
      [
        [ia(""), (d) => d],
        [iaKebab(""), (d) => d],
      ],
      { method: "POST", json }
    ),

  updateActivity: (id, json) =>
    altReq(
      [
        [ia(`/${id}`), (d) => d],
        [iaKebab(`/${id}`), (d) => d],
        [guided(`/${id}/save`), (d) => d],
        [guidedInstances(`/${id}/save`), (d) => d],
      ],
      { method: "PUT", json }
    ),

  submitActivity: (id) =>
    altReq(
      [
        [ia(`/${id}/submit`), (d) => d],
        [iaKebab(`/${id}/submit`), (d) => d],
        [guided(`/${id}/submit-observation`), (d) => d],
        [guided(`/${id}/submit`), (d) => d],
        [guidedInstances(`/${id}/submit-observation`), (d) => d],
        [guidedInstances(`/${id}/submit`), (d) => d],
      ],
      { method: "POST" }
    ),
  generateActivityPdf: (id) =>
    altReq(
      [
        [ia(`/${id}/generate-pdf`), (d) => d],
        [iaKebab(`/${id}/generate-pdf`), (d) => d],
        [ia(`/${id}/pdf`), (d) => d],
        [iaKebab(`/${id}/pdf`), (d) => d],
      ],
      { method: "POST" }
    ),

  reviewerQueue: (q = {}) => {
    const qs = queryString(q);
    return altReq(
      [
        [ia(`/reviewer-queue${qs}`), (d) => d],
        [iaKebab(`/reviewer-queue${qs}`), (d) => d],
        [iaNested(`/reviewer-queue${qs}`), (d) => d],
        [guided(`/reviewer-queue${qs}`), (d) => d],
        [guided(`/reviewerQueue${qs}`), (d) => d],
        [guided(`/queue${qs}`), (d) => d],
        [guidedInstances(`/reviewer-queue${qs}`), (d) => d],
      ],
      { method: "GET" }
    );
  },

  /* ---------- Activities (reviewer-mode; non-breaking) ---------- */
  listActivitiesForUser: (q = {}) => {
    const qs = queryString(q);
    return altReq(
      [
        [ia(`${qs}`), (d) => d],
        [iaKebab(`${qs}`), (d) => d],
        [iaNested(`${qs}`), (d) => d],
      ],
      { method: "GET" }
    );
  },

  approveActivity: (id, json = {}) =>
    altReq(
      [
        [ia(`/${id}/approve`), (d) => d],
        [iaKebab(`/${id}/approve`), (d) => d],
        [ia(`/${id}/review`), (d) => d],
        [iaKebab(`/${id}/review`), (d) => d],
        [guided(`/${id}/observer-signoff`), (d) => d],
        [guided(`/${id}/record-decision`), (d) => d],
        [guidedInstances(`/${id}/observer-signoff`), (d) => d],
      ],
      { method: "POST", json: { decision: "approved", ...json } }
    ),

  returnActivity: (id, json = {}) =>
    altReq(
      [
        [ia(`/${id}/return`), (d) => d],
        [iaKebab(`/${id}/return`), (d) => d],
        [ia(`/${id}/review`), (d) => d],
        [iaKebab(`/${id}/review`), (d) => d],
        [guided(`/${id}/observer-signoff`), (d) => d],
        [guided(`/${id}/record-decision`), (d) => d],
        [guidedInstances(`/${id}/observer-signoff`), (d) => d],
      ],
      { method: "POST", json: { decision: "returned", ...json } }
    ),

  recordActivityDecision: (id, json = {}) =>
    altReq(
      [
        [ia(`/${id}/review`), (d) => d],
        [iaKebab(`/${id}/review`), (d) => d],
        [ia(`/${id}/decision`), (d) => d],
        [iaKebab(`/${id}/decision`), (d) => d],
        [guided(`/${id}/observer-signoff`), (d) => d],
        [guided(`/${id}/record-decision`), (d) => d],
        [guided(`/${id}/observer-decision`), (d) => d],
        [guidedInstances(`/${id}/observer-signoff`), (d) => d],
        [guidedInstances(`/${id}/record-decision`), (d) => d],
      ],
      { method: "POST", json }
    ),

  /* ---------- Intern self-assessment / performance ---------- */
  listPerformanceSelf: (q = {}) => {
    const qs = queryString(q);
    return req(`/api/internPerformance/self${qs}`);
  },
  savePerformanceSelf: (json) => req("/api/internPerformance/self", { method: "POST", json }),
  perfCreateReview: (json) => req("/api/internPerformance/reviews", { method: "POST", json }),
  perfUpdateReview: (id, json) => req(`/api/internPerformance/reviews/${id}`, { method: "PUT", json }),
  perfSubmitReview: (id) => req(`/api/internPerformance/reviews/${id}/submit`, { method: "POST" }),
  perfReturnReview: (id, json) => req(`/api/internPerformance/reviews/${id}/return`, { method: "POST", json }),
  perfFinalizeReview: (id, json) => req(`/api/internPerformance/reviews/${id}/finalize`, { method: "POST", json }),
  perfReviewerQueue: (q = {}) => {
    const qs = queryString(q);
    return req(`/api/internPerformance/reviewer-queue${qs}`);
  },
  perfGetReview: (id) => req(`/api/internPerformance/reviews/${id}`),

  /* ---------- Roster (read + helpers; softened on 400/404/Unknown tenant) ---------- */
  listRosters: (q = {}) => {
    const qs = queryString(q);
    return emptyOnClientError(
      () =>
        altReq(
          [
            [`/api/rosters${qs}`, (d) => d],
            [`/rosters${qs}`, (d) => d],
          ],
          { method: "GET" }
        ),
      []
    );
  },
  rosterMine: (date) => {
    const d = toISODate(date);
    const qs = queryString({ mine: "1", date: d });
    return emptyOnClientError(
      () =>
        altReq(
          [
            [`/api/rosters${qs}`, (d) => d],
            [`/rosters${qs}`, (d) => d],
          ],
          { method: "GET" }
        ),
      []
    );
  },
  rosterForUser: (userId, date) => {
    const d = toISODate(date);
    const qs = queryString({ userId, date: d });
    return emptyOnClientError(
      () =>
        altReq(
          [
            [`/api/rosters${qs}`, (d) => d],
            [`/rosters${qs}`, (d) => d],
          ],
          { method: "GET" }
        ),
      []
    );
  },

  /* ---------- Activity Log (stats + visits) with safe fallbacks ---------- */
  getStats: (q = {}) => {
    const qs = queryString(q);
    return altReq(
      [
        [ia(`/stats${qs}`), (d) => d],
        [iaKebab(`/stats${qs}`), (d) => d],
        [iaNested(`/stats${qs}`), normalizeObject("stats")],
        [ia(`/self/summary${qs}`), normalizeObject("stats")],
        [iaKebab(`/self/summary${qs}`), normalizeObject("stats")],
      ],
      { method: "GET" }
    );
  },

  listVisitsMine: (q = {}) => {
    const qs = queryString(q);
    return emptyOnClientError(
      () =>
        altReq(
          [
            [ia(`/visits${qs}`), normalizeList("visits", "items")],
            [iaKebab(`/visits${qs}`), normalizeList("visits", "items")],
            [iaNested(`/visits${qs}`), normalizeList("visits", "items")],
            [`/api/visits/mine${qs}`, normalizeList("visits", "items")],
            [`/api/visits${qs}`, normalizeList("visits", "items")],
          ],
          { method: "GET" }
        ),
      []
    );
  },

  saveDailyLog: (json) =>
    altReq(
      [
        ["/api/intern/daily-log", (d) => d],
        [ia("/daily-log"), (d) => d],
        [iaKebab("/daily-log"), (d) => d],
        [ia("/backfill"), (d) => d],
        [iaKebab("/backfill"), (d) => d],
        [ia(""), (d) => d],
        [iaKebab(""), (d) => d],
      ],
      { method: "POST", json }
    ),

  /* ---------- Intern Rotation Checklist convenience (non-breaking) ---------- */
  getRotationChecklistCurrent: (q = {}) => {
    const qs = queryString(q);
    return emptyOnClientError(
      () =>
        altReq(
          [
            [`/api/intern-rotation-checklists/my/current${qs}`, (d) => d],
            [`/api/intern-rotation-checklists/current${qs}`, (d) => d],
            [`/api/internRotationChecklists/my/current${qs}`, (d) => d],
            [`/api/intern/rotation-checklists/my/current${qs}`, (d) => d],
          ],
          { method: "GET" }
        ),
      null
    );
  },

  upsertRotationChecklistBlock: (json = {}) =>
    altReq(
      [
        ["/api/intern-rotation-checklists/upsert", (d) => d],
        ["/api/intern-rotation-checklists/block/upsert", (d) => d],
        ["/api/internRotationChecklists/upsert", (d) => d],
        ["/api/intern/rotation-checklists/upsert", (d) => d],
      ],
      { method: "POST", json }
    ),

  incrementRotationChecklistCell: (json = {}) =>
    altReq(
      [
        ["/api/intern-rotation-checklists/increment", (d) => d],
        ["/api/intern-rotation-checklists/cell/increment", (d) => d],
        ["/api/intern-rotation-checklists/adjust", (d) => d],
        ["/api/internRotationChecklists/increment", (d) => d],
        ["/api/intern/rotation-checklists/increment", (d) => d],
      ],
      { method: "POST", json }
    ),

  /* ---------- Optional convenience for prefill (non-breaking) ---------- */
  prefillPostingWindow: async ({ userId, limit = 200 } = {}) => {
    const q = userId ? { userId, page: 1, limit } : { mine: "1", page: 1, limit };
    const rows = await internApi.listRosters(q);
    if (!Array.isArray(rows) || rows.length === 0) return null;

    const dates = rows
      .map((r) => (r && r.date ? new Date(r.date) : null))
      .filter((d) => d && !Number.isNaN(d.getTime()))
      .sort((a, b) => a - b);

    const centres = rows.map((r) => sanitizeDisplayText(r?.centre || r?.chc || r?.location || "")).filter(Boolean);
    const modeCentre = (() => {
      const m = new Map();
      centres.forEach((c) => m.set(c, (m.get(c) || 0) + 1));
      let best = "";
      let n = 0;
      for (const [k, v] of m.entries()) {
        if (v > n) {
          best = k;
          n = v;
        }
      }
      return best || "";
    })();

    const todayIso = toISODate();
    const todayRow = rows.find((r) => {
      if (!r || !r.date) return false;
      try {
        return toISODate(r.date) === todayIso;
      } catch {
        return false;
      }
    });
    const centreToday = sanitizeDisplayText(todayRow?.centre || todayRow?.chc || todayRow?.location || "");

    return {
      from: dates[0]?.toISOString().slice(0, 10),
      to: dates[dates.length - 1]?.toISOString().slice(0, 10),
      centre: centreToday || modeCentre || "",
    };
  },
};
